import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Quote from '../components/Quote';
import { SiteContent } from '../content';


const Home: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.01, rootMargin: "0px 0px 100px 0px" });

    const elements = containerRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach(el => {
      if (el.getBoundingClientRect().top >= window.innerHeight) {
        el.classList.add('below-fold');
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  const { hero, problem, invitation, vision, closing } = SiteContent.home;

  return (
    <div ref={containerRef} className="flex flex-col w-full bg-paper overflow-hidden">
      {/* SECTION 1: HERO - Editorial Asymmetry */}
      <section className="relative min-h-screen w-full flex items-center justify-start overflow-hidden px-6 md:px-12 lg:px-24 bg-rich-black">
        <img
          src="/images/home-hero.jpg"
          alt="Rich dark soil representing earth rights and regenerative business"
          className="absolute inset-0 w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />

        <div className="relative z-10 max-w-7xl w-full grid md:grid-cols-12 gap-12 pt-20">
          <div className="md:col-span-8">
            <span className="editorial-label opacity-0 animate-[reveal_0.6s_ease_0.1s_forwards] text-white">01. THE AWAKENING</span>
            <h1 className="hero-title text-[#F5F0E6] mb-10 opacity-0 animate-[reveal_0.6s_ease_0.2s_forwards]">
              A Seat for the <span className="serif-italic lowercase tracking-normal">Soil</span> at the Big Business Table
            </h1>
            <p className="serif italic text-[#F5F0E6] text-xl md:text-3xl mb-12 max-w-2xl opacity-0 animate-[reveal_0.6s_ease_0.3s_forwards]">
              {hero.subheadline}
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col justify-end pb-12">
            <div className="opacity-0 animate-[reveal_0.6s_ease_0.5s_forwards] border-l border-[#A65D3F] pl-6">

              <p className="text-[#F5F0E6]/70 text-sm md:text-base mb-8 leading-relaxed font-medium">
                {hero.description}
              </p>
              <Link to="/shop" className="btn-primary">
                {SiteContent.common.joinMovement}
              </Link>
            </div>
          </div>
        </div>

        <div className="vertical-text right-12 top-1/2 -translate-y-1/2 hidden lg:block !text-white/10 uppercase">
          REGENERATIVE FUTURES — BENEATH THE FEET
        </div>
      </section>

      {/* SECTION 2: WHAT IS ERB? */}
      <section className="section-padding relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-24 items-start">
            <div className="lg:col-span-6 z-10">
              <span className="editorial-label">WHAT IS ERB?</span>
              <div className="animate-on-scroll animate-fade-up">
                <h2 className="section-title mb-12">
                  {problem.title}
                </h2>
              </div>
              <div className="animate-on-scroll animate-fade-up delay-200">
                <p className="text-lg md:text-xl font-medium text-[#1A1A1A]/80 leading-relaxed mb-12">
                  {problem.text}
                </p>
                <ul className="space-y-6 mb-12">
                  {problem.items.map((item, i) => (
                    <li key={i} className="group">
                      <span className="font-bold text-xs tracking-widest uppercase opacity-60 group-hover:opacity-100 transition-opacity">{item}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/shop" className="btn-secondary">
                  {SiteContent.common.shopAlphabet}
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6 relative">
              <div className="animate-on-scroll animate-fade-up aspect-[3/2] rounded-lg overflow-hidden shadow-2xl bg-gradient-to-br from-[#3D2B1F] to-[#1A1A1A] grid grid-cols-3 grid-rows-2 gap-1 p-1">
                {['E','R','B','A','R','T'].map((letter, i) => (
                  <img
                    key={i}
                    src={`/letters/erb-${letter.toLowerCase()}-1.jpg`}
                    alt={`ERB letter ${letter}`}
                    loading="lazy"
                    className="w-full h-full object-cover rounded-sm opacity-80 hover:opacity-100 transition-opacity duration-500"
                  />
                ))}
              </div>

              <div className="animate-on-scroll animate-fade-up delay-300 mt-8">
                <div className="p-6 bg-[#F5F0E6] border border-[#A65D3F]/20 rounded-lg">
                  <pre className="whitespace-pre-wrap font-mono text-xs uppercase tracking-widest opacity-70">
                    {problem.products}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: HOW IT WORKS */}
      <section className="min-h-[70vh] flex items-center bg-rich-black relative py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-24">
            <span className="editorial-label !text-white/60">HOW IT WORKS</span>
            <div className="animate-on-scroll animate-fade-up">
              <h2 className="hero-title !text-white mb-8">{invitation.title}</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-16">
            {invitation.steps.map((step, i) => (
              <div key={i} className="animate-on-scroll animate-fade-up" style={{ transitionDelay: `${i * 200}ms` }}>
                <div className="text-5xl font-black text-[#A65D3F] mb-6 opacity-30">0{i + 1}</div>
                <h4 className="font-condensed text-white text-xl font-black mb-4 tracking-widest">{step.title}</h4>
                <p className="text-white/70 text-sm leading-relaxed font-medium">
                  {step.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-40 text-center animate-on-scroll animate-fade-up delay-500">
            <Quote large text={invitation.quote} />
            <p className="text-[#A65D3F] text-xl italic mt-8">“{invitation.secondaryQuote}”</p>
          </div>
        </div>
      </section>

      {/* SECTION 4: THE VISION */}
      <section className="section-padding bg-paper">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-black/5 pb-12">
            <div className="max-w-2xl">
              <span className="editorial-label">THE VISION</span>
              <h2 className="section-title">{vision.title}</h2>
            </div>
            <Link to="/goals" className="text-xs font-black uppercase tracking-[0.5em] border-b-2 border-[#A65D3F] pb-2 mb-2 py-3 hover:text-[#A65D3F] transition-colors inline-block">
              SEE THE FULL VISION
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {vision.phases.map((phase, i) => (
              <div key={i} className="group p-10 bg-white/50 border border-black/5 hover:bg-white transition-all duration-700 animate-on-scroll animate-scale-in" style={{ transitionDelay: `${i * 100}ms` }}>
                <span className="text-xs font-black tracking-widest opacity-40 group-hover:text-[#A65D3F] transition-colors">{phase.title}</span>
                {phase.status && <div className="text-xs font-bold text-[#A65D3F] mt-2 mb-2">← {phase.status}</div>}
                <p className="text-sm text-black/70 leading-relaxed font-medium mt-4">
                  {phase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5: CLOSING */}
      <section className="section-padding bg-rich-black text-center relative overflow-hidden">
        <img
          src="/images/home-hero.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-10 -scale-x-100 -scale-y-100"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
        <div className="vertical-text left-12 top-1/2 -translate-y-1/2 !text-white/5">
          STAKE YOUR CLAIM — REPRESENT THE EARTH
        </div>

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="animate-on-scroll animate-fade-up">
            <Quote large text={closing.quote} className="mb-20" />
            <p className="text-white/70 text-lg md:text-xl italic mb-16">
              {closing.description}
            </p>
          </div>
          <div className="pt-8 animate-on-scroll animate-scale-in delay-200">
            <Link to="/shop" className="btn-primary !px-12">
              SHOP NOW
            </Link>
            <div className="mt-8">
              <p className="text-white/60 text-xs uppercase tracking-widest mb-4">Not ready to buy?</p>
              <a href="#footer-newsletter" className="text-[#A65D3F] hover:text-white transition-colors text-xs uppercase tracking-widest border-b border-[#A65D3F]">Stay Connected →</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;